import { SubscriptionSummary } from "@/types/businessData";

/**
 * Account types that have privileged access (Pro-level features).
 * Includes legacy account types (pre-Stripe) and current Pro accounts.
 */
export const PRIVILEGED_ACCOUNT_TYPES = ["pro", "premium", "unlimited"] as const;

/**
 * Legacy account types (pre-Stripe integration).
 * These accounts have Pro-level access but no Stripe subscription.
 * "pro" is NOT legacy - it's the current Stripe-based account type.
 */
export const LEGACY_ACCOUNT_TYPES = ["premium", "unlimited"] as const;

/**
 * Enum for different subscription lifecycle states
 */
export enum SubscriptionType {
  ACTIVE_PAYING = "active_paying",
  ACTIVE_TRIAL = "active_trial",
  EXPIRED_TRIAL = "expired_trial",
  CANCELED_PAYING = "canceled_paying",
  SCHEDULED_CANCELLATION = "scheduled_cancellation",
  NO_SUBSCRIPTION = "no_subscription",
  PAST_DUE = "past_due",
  INCOMPLETE = "incomplete",
  UNPAID = "unpaid",
  PAUSED = "paused",
}

export interface SubscriptionInfo {
  type: SubscriptionType;
  isActive: boolean;
  hasExpired: boolean;
  wasPaying: boolean;
  isTrialing: boolean;
  needsUpgrade: boolean;
  /** i18n key (common namespace) for the human-readable status message. */
  messageKey: string;
  /** i18n key (common namespace) for the primary call-to-action label. */
  callToActionKey: string;
}

/**
 * Central utility function to determine subscription type and status
 * Use this function throughout the app for consistent subscription checks
 */
export function getSubscriptionInfo(
  subscription: SubscriptionSummary | null | undefined
): SubscriptionInfo {
  // No subscription exists
  if (!subscription) {
    return {
      type: SubscriptionType.NO_SUBSCRIPTION,
      isActive: false,
      hasExpired: false,
      wasPaying: false,
      isTrialing: false,
      needsUpgrade: true,
      messageKey: "subscription.status.none",
      callToActionKey: "subscription.cta.start",
    };
  }

  const status = subscription.status;
  const currentPeriodEnd = subscription.currentPeriodEnd;
  const trialEnd = subscription.trialEnd;
  const cancelAtPeriodEnd = subscription.cancelAtPeriodEnd;

  // Calculate if subscription has expired
  const hasExpired =
    currentPeriodEnd && new Date(currentPeriodEnd.seconds * 1000) < new Date();

  // Check if currently in trial
  const isCurrentlyTrialing = status === "trialing";

  // Check if they ever paid (after trial ended or no trial at all)
  const wasPaying = (() => {
    if (!trialEnd) return true; // No trial = was/is paying

    const trialEndDate = new Date(trialEnd.seconds * 1000);
    const periodStartDate = new Date(
      subscription.currentPeriodStart.seconds * 1000
    );

    // If current period started after trial ended, they paid at least once
    return periodStartDate > trialEndDate;
  })();

  // Handle different status states
  switch (status) {
    case "active":
      if (cancelAtPeriodEnd) {
        return {
          type: SubscriptionType.SCHEDULED_CANCELLATION,
          isActive: true,
          hasExpired: false,
          wasPaying: true,
          isTrialing: false,
          needsUpgrade: false,
          messageKey: "subscription.status.scheduledCancellation",
          callToActionKey: "subscription.cta.manage",
        };
      }
      return {
        type: SubscriptionType.ACTIVE_PAYING,
        isActive: true,
        hasExpired: false,
        wasPaying: true,
        isTrialing: false,
        needsUpgrade: false,
        messageKey: "subscription.status.active",
        callToActionKey: "subscription.cta.manage",
      };

    case "trialing":
      if (cancelAtPeriodEnd) {
        return {
          type: SubscriptionType.SCHEDULED_CANCELLATION,
          isActive: true,
          hasExpired: false,
          wasPaying: false,
          isTrialing: true,
          needsUpgrade: false,
          messageKey: "subscription.status.trialScheduledCancellation",
          callToActionKey: "subscription.cta.manage",
        };
      }
      return {
        type: SubscriptionType.ACTIVE_TRIAL,
        isActive: true,
        hasExpired: false,
        wasPaying: false,
        isTrialing: true,
        needsUpgrade: false,
        messageKey: "subscription.status.trial",
        callToActionKey: "subscription.cta.manage",
      };

    case "canceled":
      if (hasExpired) {
        // Determine if they were paying or just trialing
        if (wasPaying) {
          return {
            type: SubscriptionType.CANCELED_PAYING,
            isActive: false,
            hasExpired: true,
            wasPaying: true,
            isTrialing: false,
            needsUpgrade: true,
            messageKey: "subscription.status.canceledPaying",
            callToActionKey: "subscription.cta.renew",
          };
        } else {
          return {
            type: SubscriptionType.EXPIRED_TRIAL,
            isActive: false,
            hasExpired: true,
            wasPaying: false,
            isTrialing: false,
            needsUpgrade: true,
            messageKey: "subscription.status.expiredTrial",
            callToActionKey: "subscription.cta.activate",
          };
        }
      }
      // Canceled but still within period (still has access but can't restart from portal)
      // User needs to create a new subscription via checkout
      return {
        type: SubscriptionType.SCHEDULED_CANCELLATION,
        isActive: true, // Still has access until period end
        hasExpired: false,
        wasPaying: wasPaying,
        isTrialing: isCurrentlyTrialing,
        needsUpgrade: true, // Changed to true - they'll need to renew
        messageKey: wasPaying
          ? "subscription.status.canceledInPeriodPaying"
          : "subscription.status.canceledInPeriodTrial",
        callToActionKey: "subscription.cta.renew",
      };

    case "past_due":
      return {
        type: SubscriptionType.PAST_DUE,
        isActive: false,
        hasExpired: false,
        wasPaying: true,
        isTrialing: false,
        needsUpgrade: true,
        messageKey: "subscription.status.pastDue",
        callToActionKey: "subscription.cta.manage",
      };

    case "incomplete":
      return {
        type: SubscriptionType.INCOMPLETE,
        isActive: false,
        hasExpired: false,
        wasPaying: false,
        isTrialing: false,
        needsUpgrade: true,
        messageKey: "subscription.status.incomplete",
        callToActionKey: "subscription.cta.manage",
      };

    case "incomplete_expired":
      return {
        type: SubscriptionType.INCOMPLETE,
        isActive: false,
        hasExpired: true,
        wasPaying: false,
        isTrialing: false,
        needsUpgrade: true,
        messageKey: "subscription.status.incompleteExpired",
        callToActionKey: "subscription.cta.manage",
      };

    case "unpaid":
      return {
        type: SubscriptionType.UNPAID,
        isActive: false,
        hasExpired: false,
        wasPaying: true,
        isTrialing: false,
        needsUpgrade: true,
        messageKey: "subscription.status.unpaid",
        callToActionKey: "subscription.cta.manage",
      };

    case "paused":
      return {
        type: SubscriptionType.PAUSED,
        isActive: false,
        hasExpired: false,
        wasPaying: false,
        isTrialing: false,
        needsUpgrade: true,
        messageKey: "subscription.status.paused",
        callToActionKey: "subscription.cta.manage",
      };

    default:
      return {
        type: SubscriptionType.NO_SUBSCRIPTION,
        isActive: false,
        hasExpired: false,
        wasPaying: false,
        isTrialing: false,
        needsUpgrade: true,
        messageKey: "subscription.status.unknown",
        callToActionKey: "subscription.cta.contactSupport",
      };
  }
}

/**
 * Quick check if user has active access to features
 */
export function hasActiveSubscription(
  subscription: SubscriptionSummary | null | undefined
): boolean {
  const info = getSubscriptionInfo(subscription);
  return info.isActive;
}

/**
 * Check if user needs to upgrade/renew
 */
export function needsSubscriptionUpgrade(
  subscription: SubscriptionSummary | null | undefined
): boolean {
  const info = getSubscriptionInfo(subscription);
  return info.needsUpgrade;
}

/**
 * Check if account is a legacy account (pre-Stripe integration).
 * Only "premium" and "unlimited" without a subscription object are legacy.
 * "pro" is the current Stripe-based account type and is NOT legacy.
 */
export function isLegacyAccount(
  accountType: string | undefined,
  subscription: SubscriptionSummary | null | undefined
): boolean {
  return LEGACY_ACCOUNT_TYPES.includes(accountType as typeof LEGACY_ACCOUNT_TYPES[number]) && !subscription;
}
